import React from 'react';
import PropTypes from 'prop-types';
import { Bert } from 'meteor/themeteorchef:bert';
import { Meteor } from 'meteor/meteor';
import { withTracker } from 'meteor/react-meteor-data';
import { isEmpty, isNaN, map, isEqual, indexOf, cloneDeep, find, orderBy } from 'lodash';
import turf from '@turf/turf';
import uuid from 'uuid/v1';

import { Box, IconButton, Button, Tooltip, Typography, Stack, Modal } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import handleErrorInClient from '../../../modules/handle-error-in-client';
import methodPromise from '../../../modules/method-promise-wrapper';

import Videos, { VIDEO_COORDINATE_DATA_TYPES } from '../../../api/Videos/Videos';
import * as videoCalculate from '../../../api/Videos/videos-calculations';
import * as videoOps from '../../../api/Videos/video-operations';
import * as annotationOps from '../../../api/Annotations/annotation-operations';

import Annotations from '../../../api/Annotations/Annotations';
import { LIBRARY_ICON_TYPES } from '../../../api/AnnotationTemplates/AnnotationTemplates';
import ViewAnnotationTemplate from '../../pages/ViewAnnotationTemplate';
import * as templateOps from '../../../api/AnnotationTemplates/annotation-template-operations';
import Loading from '../Loading';
import useTimeStore from '../ZustandProvider';
import LoadDataBase from '../ConnectionDataBase/LoadDataBase';
import ConfigurationsConnectors from '../../../api/ConfigurationsConnectors/ConfigurationsConnectors';

const AnnotationDirectButtonsComponent = props => {
  const {
    annotationList,
    isMobile,
    isPublicMap,
    MapProps,
    loading,
    userProps,
    video,
    configConnectorsList
  } = props;
  if (loading === true) {
    return <Loading />;
  }
  const {
    videoId,
    savingSystem,
    intervalTime,
    enableEditTemplate,
    enableBaseDataTemplate,
    showConsultingData,
    configConnectors
  } = MapProps.map.settingsAdvancedMap.directButtons;
  const coordOnMap = useTimeStore(state => state.coordOnMap);
  const geometryConfig = useTimeStore(state => state.geometryConfig);
  const setGeometryConfig = useTimeStore(state => state.setGeometryConfig);

  const [annotationTemplateSelected, setAnnotationTemplateSelected] = React.useState({});
  const [openModal, setOpenModal] = React.useState(false);

  const [newAnnotation, setNewAnnotation] = React.useState({});
  const [geometryNewAnnotation, setGeometryNewAnnotation] = React.useState({});

  const [configConnectorState, setConfigConnectorState] = React.useState({});

  const showButtonConsulting = showConsultingData && enableBaseDataTemplate;

  const fechaAGpsEnMicrosegundos = () => {
    const fechaConHora = new Date();
    const fechaReferenciaGps = new Date('1980-01-06T00:00:00Z');
    const diferencia = fechaConHora.getTime() - fechaReferenciaGps.getTime();
    const diferenciaEnMicroSegundos = diferencia * 1000;
    return diferenciaEnMicroSegundos;
  };

  const obtenerUbicacion = async () => {
    let textError = '';
    if ('geolocation' in navigator) {
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          position => {
            const latitud = position.coords.latitude;
            const longitud = position.coords.longitude;
            const coordenadas = [longitud, latitud];
            resolve(coordenadas);
          },
          error => {
            switch (error.code) {
              case error.PERMISSION_DENIED:
                textError = 'El usuario denegó la solicitud de geolocalización.';
                reject(new Error('El usuario denegó la solicitud de geolocalización.'));
                break;
              case error.POSITION_UNAVAILABLE:
                textError = 'La información de la ubicación no está disponible.';
                reject(new Error('La información de la ubicación no está disponible.'));
                break;
              case error.TIMEOUT:
                textError = 'Se ha agotado el tiempo de espera para obtener la ubicación.';
                reject(new Error('Se ha agotado el tiempo de espera para obtener la ubicación.'));
                break;
              case error.UNKNOWN_ERROR:
                textError = 'Se produjo un error desconocido al obtener la ubicación.';
                reject(new Error('Se produjo un error desconocido al obtener la ubicación.'));
                break;
              default:
                textError = 'Se produjo un error desconocido al obtener la ubicación.';
                reject(new Error('Se produjo un error desconocido al obtener la ubicación.'));
                break;
            }
            Bert.alert(textError, 'danger');
          }
        );
      });
    }
    return Promise.reject(new Error('La geolocalización no está soportada por este navegador.'));
  };

  const videoTimeFunction = currentTimeGPS => {
    let time = 0;
    if (savingSystem === 'incremental') {
      const numerosVideotime = !isEmpty(annotationList)
        ? annotationList.map(objeto => objeto.geoJSON.properties.videoTime)
        : [0];
      time = Math.max(...numerosVideotime) + parseInt(intervalTime, 10) * 1000000;
    } else if (savingSystem === 'location') {
      const newPointTurf = turf.point([coordOnMap.lng, coordOnMap.lat]);
      const newPointVideo = turf.nearestPointOnLine(video.track, newPointTurf);
      time = videoCalculate.calculateTimeVideoLine(video, newPointVideo, annotationList);
    } else if (savingSystem === 'gpsTime') {
      const numerosTimeGPS = !isEmpty(annotationList)
        ? annotationList.map(objeto => objeto.timeGPS)
        : [0];
      const initialTimeGPS = Math.min(...numerosTimeGPS);
      time =
        initialTimeGPS !== 0 && !isNaN(initialTimeGPS)
          ? currentTimeGPS - initialTimeGPS
          : currentTimeGPS;
    } else {
      time += 5 * 1000000;
    }
    return time;
  };

  const updateCoordsDateTemplate = (coords, templateSelected) => {
    if (!isEmpty(templateSelected)) {
      const newTemplateSelected = cloneDeep(templateSelected);
      const cloneNewAnnotation = cloneDeep(newAnnotation);
      const elements = templateOps.getElements(newTemplateSelected);
      const sortedTemplateElements = orderBy(elements, ['row', 'column', 'order'], ['asc']);
      const actualDate = new Date();
      const dateIsoString = actualDate.toISOString();
      const dateString = dateIsoString.substring(0, dateIsoString.lastIndexOf('T'));
      const hourString = dateIsoString.substring(
        dateIsoString.lastIndexOf('T') + 1,
        dateIsoString.lastIndexOf('Z')
      );
      const { connectionsFields = [] } = configConnectorState;
      map(connectionsFields, field => {
        if (!isEqual(field.value, 'none') && !isEqual(field.elementIndex, -1)) {
          const newElement = sortedTemplateElements[field.elementIndex];
          switch (field.value) {
            case 'date now':
              if (!isEmpty(newElement) && newElement.dataType === 'date') {
                newElement.value = dateString;
                const elementAsociated = sortedTemplateElements[field.elementAsociatedIndex];
                elementAsociated.value = hourString;
                sortedTemplateElements[field.elementAsociatedIndex] = elementAsociated;
              }
              break;
            case 'value latitude':
              newElement.value = coords.lat;
              break;
            case 'value longitude':
              newElement.value = coords.lng;
              break;

            default:
              break;
          }
          sortedTemplateElements[field.elementIndex] = newElement;
        }
      });

      newTemplateSelected.elements = sortedTemplateElements;
      cloneNewAnnotation.template = newTemplateSelected;
      setAnnotationTemplateSelected(newTemplateSelected);
      setNewAnnotation(cloneNewAnnotation);
    }
  };

  const postDataConnection = async (server, data) => {
    try {
      const response = await fetch(
        'https://sd1.ipsgeoprocesing.ipsilum.com/MG/utilities/upload_to_database',
        {
          method: 'POST',
          body: JSON.stringify({
            connection: server,
            data
          }),
          headers: { 'Content-Type': 'application/json' }
        }
      );
      const answer = await response.json();
      if (typeof answer === 'string') {
        if (answer.includes('error')) {
          Bert.alert('Ha habido un error en el guardado de datos', 'danger');
        } else {
          Bert.alert('Datos guardados correctamente', 'success');
        }
      }
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  const loadDataGeometry = async () => {
    let geometryToReturn = {};
    let coordinates = [];
    try {
      coordinates =
        geometryConfig === 'directButton' && !isEmpty(coordOnMap)
          ? [coordOnMap.lng, coordOnMap.lat]
          : await obtenerUbicacion();
    } catch (error) {
      handleErrorInClient(error);
    }
    if (!isEmpty(coordinates)) {
      geometryToReturn = `POINT(${coordinates[0]} ${coordinates[1]})`;
    }
    return geometryToReturn;
  };

  const loadDataConnection = async (item, timeVideo) => {
    if (isEmpty(item)) throw new Error('No annotation template selected.');
    const valuesConnection = [];
    const { connectorId, source, connectionsFields } = configConnectorState;
    const server = await methodPromise('connectors.findOne', connectorId);

    const elementsTemplate = templateOps.getElements(item);
    if (!isEmpty(server)) {
      const { user, databaseName, ip, port, password, schema, owner } = server;

      let coordsToSave = {};
      if (!isEmpty(geometryNewAnnotation)) {
        coordsToSave = cloneDeep(geometryNewAnnotation);
      }
      if (!isEmpty(coordOnMap)) {
        coordsToSave = `POINT(${coordOnMap.lng} ${coordOnMap.lat})`;
      }
      const elemntsOrder = orderBy(elementsTemplate, ['row', 'column', 'order'], ['asc']);

      map(connectionsFields, async field => {
        if (field.value !== 'none') {
          const element = elemntsOrder[field.elementIndex];
          const { nameColumnTable } = field;
          switch (field.value) {
            case 'owner':
              valuesConnection.push({ [nameColumnTable]: owner });
              break;
            case 'geometry annotation':
              valuesConnection.push({ [nameColumnTable]: coordsToSave });
              break;
            case 'videoTime':
              valuesConnection.push({ [nameColumnTable]: timeVideo });
              break;
            case 'date now':
            case 'date/hour':
              if (element.elementType === 'input') {
                if (element.dataType === 'date') {
                  const elementHour = elemntsOrder[field.elementAsociatedIndex];
                  const dateToSave = `${element.value} ${elementHour.value}`;
                  if (dateToSave !== ' ') {
                    valuesConnection.push({ [nameColumnTable]: dateToSave });
                  }
                }
              }
              break;
            case 'value latitude':
            case 'value longitude':
              if (enableEditTemplate) {
                const { value } = element;
                valuesConnection.push({ [nameColumnTable]: value });
              }
              break;
            default:
              if (enableEditTemplate) {
                const value = element[field.value];
                valuesConnection.push({ [nameColumnTable]: value });
              }
              break;
          }
        }
      });

      const serverToConnection = {
        user,
        ip,
        port,
        databaseName,
        password,
        schema,
        tableName: source
      };
      if (!isEmpty(valuesConnection))
        await postDataConnection(serverToConnection, valuesConnection);
    } else {
      Bert.alert('No configuration exists', 'danger');
    }
  };

  const createAnnotation = async item => {
    try {
      const coordenadas =
        geometryConfig === 'directButton'
          ? [coordOnMap.lng, coordOnMap.lat]
          : await obtenerUbicacion();

      if (isEmpty(geometryConfig))
        throw new Error('No geomtry template selected, add point on map or enable geolocation.');
      if (enableBaseDataTemplate && !isEmpty(configConnectorState)) {
        loadDataConnection(item, annotationOps.getVideoTime(newAnnotation));
      }
      const isVideoPoint = video.coordinatesDataType === VIDEO_COORDINATE_DATA_TYPES.POINT;
      try {
        if (isEmpty(item)) throw new Error('No annotation template selected.');
        const annotationToSave = {
          geoJSON: {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: coordenadas
            },
            properties: {
              template: item,
              video: videoId,
              videoTime: annotationOps.getVideoTime(newAnnotation),
              videoPosition: annotationOps.getVideoPosition(newAnnotation),
              isProyected: !isVideoPoint
            }
          },
          origin: 'manual',
          isTracking: false
        };
        const userId = isPublicMap === true ? userProps.userLocalId.id : userProps.userId;
        await methodPromise('annotations.insert', annotationToSave, isPublicMap, userId);
        Bert.alert('Annotation created', 'success');
      } catch (error) {
        handleErrorInClient(error);
      }
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  const handleAnnotationTemplate = template => {
    const annotationTemplateDefault = cloneDeep(template);

    if (enableBaseDataTemplate) {
      const configConnectorAdvanced = find(configConnectors, config =>
        isEqual(config.templateId, templateOps.getId(template))
      );
      const configConnectorSelected = find(configConnectorsList, config =>
        isEqual(config._id, configConnectorAdvanced.configId)
      );
      setConfigConnectorState(configConnectorSelected);
    }

    annotationTemplateDefault.elements.forEach(item => {
      // eslint-disable-next-line no-param-reassign
      item.value = '';
    });

    setAnnotationTemplateSelected(annotationTemplateDefault);
    if (!enableEditTemplate) {
      createAnnotation(annotationTemplateDefault);
    }
  };

  const updateAnnotationSelected = annotation => {
    const templateAnnotation = annotationOps.getTemplate(annotation);
    setAnnotationTemplateSelected(templateAnnotation);
    setNewAnnotation(annotation);
  };

  const updateAnnotationFields = (elementItem, property, newValue) => {
    const newFieldsTemplate = cloneDeep(annotationTemplateSelected);
    const templateElementsArray = templateOps.getElements(newFieldsTemplate);
    const elementUpdate = find(templateElementsArray, element => isEqual(element, elementItem));
    const newElementTemplate = map(templateElementsArray, element => {
      const newElement = element;
      if (
        isEqual(
          indexOf(templateElementsArray, element),
          indexOf(templateElementsArray, elementUpdate)
        )
      ) {
        newElement[property] = newValue;
      }
      return newElement;
    });
    newFieldsTemplate.elements = newElementTemplate;
    setAnnotationTemplateSelected(newFieldsTemplate);
  };

  const startNewAnnotation = async () => {
    try {
      const valueGeometry = await loadDataGeometry();
      const currentTimeGPS = fechaAGpsEnMicrosegundos();
      const videoTimeValue = videoTimeFunction(currentTimeGPS);
      const newAnnotationState = {
        geoJSON: {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: []
          },
          properties: {
            video: videoId,
            videoTime: parseInt(videoTimeValue, 10),
            videoPosition: {
              x: 370.75,
              y: 137,
              width: 549,
              height: 309
            },
            template: annotationTemplateSelected
          }
        },
        origin: 'manual'
      };
      setNewAnnotation(newAnnotationState);
      setGeometryNewAnnotation(valueGeometry);
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  const reloadCoordinates = async () => {
    const newValueGeometry = await loadDataGeometry();
    setGeometryNewAnnotation(newValueGeometry);
  };

  React.useEffect(() => {
    if (!isEmpty(annotationTemplateSelected)) {
      startNewAnnotation();
    }
  }, [annotationTemplateSelected]);

  React.useEffect(() => {
    if (!isEmpty(geometryNewAnnotation) && !isEmpty(annotationTemplateSelected)) {
      let latitude = 0;
      let longitude = 0;
      if (typeof geometryNewAnnotation === 'string') {
        longitude = geometryNewAnnotation.substring(
          geometryNewAnnotation.lastIndexOf('(') + 1,
          geometryNewAnnotation.lastIndexOf(' ')
        );
        latitude = geometryNewAnnotation.substring(
          geometryNewAnnotation.lastIndexOf(' ') + 1,
          geometryNewAnnotation.lastIndexOf(')')
        );
      } else {
        latitude = geometryNewAnnotation.lat;
        longitude = geometryNewAnnotation.lng;
      }
      updateCoordsDateTemplate({ lat: latitude, lng: longitude }, annotationTemplateSelected);
    }
  }, [geometryNewAnnotation]);

  React.useEffect(() => {
    if (!isEmpty(coordOnMap)) {
      reloadCoordinates();
    }
  }, [coordOnMap]);

  return (
    <>
      <Stack direction="row" display="flex" justifyContent="space-between">
        {videoOps.getAnnotationTemplates(video).map(template => {
          return (
            <Tooltip key={uuid()} title={template.name}>
              <IconButton
                key={uuid()}
                variant={isEqual(annotationTemplateSelected, template) ? 'contained' : 'outlined'}
                color={isEqual(annotationTemplateSelected, template) ? 'primary' : 'default'}
                size="small"
                onClick={() => {
                  handleAnnotationTemplate(template);
                }}
              >
                {template.libraryIcon === LIBRARY_ICON_TYPES.SVG ? (
                  <img
                    src={template.iconSvg}
                    alt={template.icon}
                    style={{ marginRight: '5px', width: '20px', height: '20px' }}
                  />
                ) : (
                  <FontAwesomeIcon icon={template.icon} />
                )}
              </IconButton>
            </Tooltip>
          );
        })}
      </Stack>
      <Box display="row" justifyContent="space-evenly" mb={2}>
        <Button
          variant="outlined"
          size="small"
          style={{
            color: geometryConfig === 'directButton' ? 'white' : '#2A3F90',
            backgroundColor: geometryConfig === 'directButton' ? '#2A3F90' : 'white',
            borderColor: '#2A3F90',
            width: '100%',
            margin: isMobile ? '8px 0' : '0'
          }}
          onClick={() => {
            if (geometryConfig === 'directButton') {
              setGeometryConfig('none');
            } else {
              setGeometryConfig('directButton');
            }
          }}
        >
          Seleccionar punto en el mapa
        </Button>
      </Box>
      {enableEditTemplate && (
        <Box width="100%" marginTop="5%">
          <Box>
            <Stack
              direction="row"
              display="flex"
              justifyContent="flex-start"
              marginTop="1%"
              marginLeft="2%"
            >
              <Typography variant="h5">
                {templateOps.getName(annotationTemplateSelected)}
              </Typography>
            </Stack>
          </Box>
          {!isEmpty(annotationTemplateSelected) && (
            <Box marginLeft="5%" marginRight="5%">
              <ViewAnnotationTemplate
                selectedTemplate={templateOps.getId(annotationTemplateSelected)}
                annotationTemplate={annotationTemplateSelected}
                updateAnnotationFields={updateAnnotationFields}
                startingPreviewMode="edition"
                video={video}
                height="50vh"
                isShowButtons={false}
                isConnectionTemplate={openModal}
                updatedAnnotation={newAnnotation}
                updateAnnotationSelected={updateAnnotationSelected}
              />
            </Box>
          )}
          <Box height="5vh">
            <Stack
              direction="row"
              display="flex"
              justifyContent="flex-end"
              marginTop="2%"
              marginRight="4%"
            >
              {showButtonConsulting && !isEmpty(annotationTemplateSelected) && (
                <>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={() => setOpenModal(true)}
                    style={{ marginRight: '5%' }}
                  >
                    Consultar datos
                  </Button>
                </>
              )}
              {!isEmpty(annotationTemplateSelected) && (
                <Button
                  variant="outlined"
                  color="success"
                  size="small"
                  onClick={() => createAnnotation(annotationTemplateSelected)}
                >
                  Crear anotacion
                </Button>
              )}
            </Stack>
          </Box>
          {!isEmpty(configConnectorState) && (
            <Modal open={openModal} onClose={() => setOpenModal(false)}>
              <Box
                style={{
                  position: 'absolute',
                  backgroundColor: 'white',
                  height: '80%',
                  width: '80%',
                  top: '10%',
                  left: '10%'
                }}
              >
                <LoadDataBase
                  connectorId={configConnectorState.connectorId}
                  source={configConnectorState.source}
                />
                <Box height="10%" marginTop="5%">
                  <Stack
                    direction="row"
                    display="flex"
                    justifyContent="flex-end"
                    marginRight="2%"
                    marginTop="4%"
                  >
                    <Button
                      onClick={() => setOpenModal(false)}
                      size="small"
                      variant="outlined"
                      color="error"
                    >
                      Close
                    </Button>
                  </Stack>
                </Box>
              </Box>
            </Modal>
          )}
        </Box>
      )}
    </>
  );
};

export default withTracker(({ MapProps }) => {
  const { videoId } = MapProps.map.settingsAdvancedMap.directButtons;
  const videoSub = Meteor.isClient && Meteor.subscribe('videos.view', videoId);
  const annotationSub = Meteor.subscribe('annotations', videoId);
  const video = Videos.findOne(videoId);
  const connectorsSub = Meteor.subscribe('connectors');
  const configConnectorsSub = Meteor.subscribe('configurationsConnectors');

  return {
    loading:
      !annotationSub.ready() &&
      !videoSub.ready() &&
      !connectorsSub.ready() &&
      !configConnectorsSub.ready(),
    annotationList: Annotations.find({}, { sort: { videoTime: 1 } }).fetch(),
    video,
    configConnectorsList: ConfigurationsConnectors.find({}, { sort: { name: 1 } }).fetch()
  };
})(AnnotationDirectButtonsComponent);
AnnotationDirectButtonsComponent.defaultProps = {
  annotationList: [],
  isMobile: false,
  isPublicMap: false,
  loading: false,
  userProps: {},
  video: {},
  configConnectorsList: []
};
AnnotationDirectButtonsComponent.propTypes = {
  annotationList: PropTypes.array,
  isMobile: PropTypes.bool,
  isPublicMap: PropTypes.bool,
  MapProps: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  userProps: PropTypes.object,
  video: PropTypes.object,
  configConnectorsList: PropTypes.array
};
